import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  LinearProgress,
  Button,
} from "@mui/material";
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import PriceChangeIcon from "@mui/icons-material/PriceChange";

const UserPlan = () => {
  const [planDetails, setPlanDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPlanDetails = async () => {
      setTimeout(() => {
        setPlanDetails({
          interactionsContracted: 10000,
          interactionsConsumed: 12000,
          interactionsByChannel: {
            whatsapp: 5000,
            email: 3000,
            chat: 2000,
            voice: 2000,
          },
          interactionsHistory: Array.from({ length: 100 }, (_, index) => ({
            id: index + 1,
            date: `2024-12-${String(index % 31 + 1).padStart(2, "0")}`,
            channel: ["WhatsApp", "Email", "Chat", "Voice"][index % 4],
            protocol: `PROTO-${index + 1}`,
            message: `Mensagem simulada ${index + 1}`,
            template: index % 2 === 0 ? "Sim" : "Não",
          })),
          planValue: 500.0,
          extraInteractionValue: 0.05,
        });
        setIsLoading(false);
      }, 1000);
    };

    fetchPlanDetails();
  }, []);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const {
    interactionsContracted,
    interactionsConsumed,
    interactionsByChannel,
    interactionsHistory,
    planValue,
    extraInteractionValue,
  } = planDetails;

  const interactionsExceeded =
    interactionsConsumed > interactionsContracted
      ? interactionsConsumed - interactionsContracted
      : 0;

  const extraCost = interactionsExceeded * extraInteractionValue;
  const totalCost = planValue + extraCost;

  const channelData = Object.entries(interactionsByChannel).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value,
  }));

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  const handleUpgradePlan = () => alert("Funcionalidade de upgrade do plano.");
  const handleDowngradePlan = () => alert("Funcionalidade de downgrade do plano.");

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        Detalhes do Meu Plano
      </Typography>
      <Divider sx={{ my: 2 }} />

      <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={4}>
        {/* Resumo do Plano */}
        <Box flex={1}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Resumo do Plano
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <PriceChangeIcon color="primary" />
                <Typography>
                  <strong>Interações Contratadas:</strong> {interactionsContracted}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <PriceChangeIcon color="primary" />
                <Typography>
                  <strong>Interações Consumidas:</strong> {interactionsConsumed}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <PriceChangeIcon color={interactionsExceeded > 0 ? "error" : "primary"} />
                <Typography>
                  <strong>Interações Excedentes:</strong> {interactionsExceeded}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <PriceChangeIcon color="primary" />
                <Typography>
                  <strong>Valor do Plano:</strong> R$ {planValue.toFixed(2)}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <PriceChangeIcon color="primary" />
                <Typography>
                  <strong>Valor dos Excedentes:</strong> R$ {extraCost.toFixed(2)}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <PriceChangeIcon color="success" />
                <Typography>
                  <strong>Total:</strong> R$ {totalCost.toFixed(2)}
                </Typography>
              </Box>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<UpgradeIcon />}
                  onClick={handleUpgradePlan}
                >
                  Upgrade
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<TrendingDownIcon />}
                  onClick={handleDowngradePlan}
                >
                  Downgrade
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Gráfico de Consumo por Canal */}
        <Box flex={1}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6">Visão Geral do Consumo</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={channelData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  dataKey="value"
                >
                  {channelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Box>
      </Box>

      {/* Linha de Progresso */}
      <Box mt={4}>
        <Typography variant="h6">Progresso do Consumo</Typography>
        <LinearProgress
          variant="determinate"
          value={(interactionsConsumed / interactionsContracted) * 100}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: "#ccc",
            "& .MuiLinearProgress-bar": {
              backgroundColor: interactionsConsumed > interactionsContracted ? "red" : "blue",
            },
          }}
        />
      </Box>

      {/* Histórico de Interações */}
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          Histórico de Interações
        </Typography>
        <Paper elevation={3} sx={{ p: 2 }}>
          <DataGrid
            rows={interactionsHistory}
            columns={[
              { field: "date", headerName: "Data", width: 150 },
              { field: "channel", headerName: "Canal", width: 120 },
              { field: "protocol", headerName: "Protocolo", width: 150 },
              { field: "message", headerName: "Mensagem", flex: 1 },
              { field: "template", headerName: "Template", width: 120 },
            ]}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            autoHeight
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default UserPlan;
